import React, { useMemo } from 'react';

import { PluginHook, TableInstance, ColumnInterface } from 'react-table';
import { CSSObject } from 'styled-components';

import Checkbox from '@adsk/alloy-react-checkbox';

import { STICKY_TYPES, TABLE_SIZE_THEMES } from './consts';
import { TableSize } from './types';

type RenderCheckboxHeaderCell = {
  isPaginatable?: boolean;
};

const renderCheckboxHeaderCell = ({
  isPaginatable,
}: RenderCheckboxHeaderCell) => {
  const HeaderCheckbox = ({
    getToggleAllRowsSelectedProps,
    getToggleAllPageRowsSelectedProps,
    data,
    rowsById,
    isAllRowsSelected,
    state,
  }: TableInstance) => {
    const propGetter = isPaginatable
      ? getToggleAllPageRowsSelectedProps
      : getToggleAllRowsSelectedProps;
    const { title, style, onChange } = propGetter();

    const nonDisabledRowIds = useMemo(() => {
      const rowIds = { ...rowsById };
      Object.keys(state?.rowState || {}).forEach((rowId) => {
        const rowState: { isDisabled?: boolean } | undefined =
          state?.rowState?.[rowId];
        if (rowState?.isDisabled) {
          delete rowIds[rowId];
        }
      });
      return rowIds;
    }, [rowsById, state]);

    const isAllNonDisabledRowsSelected = useMemo(() => {
      if (isAllRowsSelected) return true;
      return Object.keys(nonDisabledRowIds).every(
        (id) => state.selectedRowIds[id]
      );
    }, [state, isAllRowsSelected, nonDisabledRowIds]);

    const nonDisabledSelectedRowIds = useMemo(() => {
      const rowIds = { ...state.selectedRowIds };
      Object.keys(state?.rowState || {}).forEach((rowId) => {
        const rowState: { isDisabled?: boolean } | undefined =
          state?.rowState?.[rowId];
        if (rowState?.isDisabled && state.selectedRowIds[rowId]) {
          delete rowIds[rowId];
        }
      });
      return rowIds;
    }, [state]);

    const checkedState = useMemo(() => {
      if (!data.length) return false;
      const isIndeterminate =
        !isAllNonDisabledRowsSelected &&
        Object.keys(nonDisabledSelectedRowIds).length > 0;
      return (
        isAllNonDisabledRowsSelected ||
        (isIndeterminate && Checkbox.INDETERMINATE)
      );
    }, [data, isAllNonDisabledRowsSelected, nonDisabledSelectedRowIds]);

    return (
      <Checkbox
        data-testid="toggleAllRowsCheckbox"
        name={title}
        style={style as CSSObject}
        onClick={(e) => e.stopPropagation()}
        onChange={(_val, e) => onChange?.(e)}
        checked={checkedState}
        disabled={!data.length}
      />
    );
  };
  return HeaderCheckbox;
};

const renderCheckboxBodyCell = ({ row }: TableInstance) => {
  const { indeterminate, title, style, onChange, checked } =
    row.getToggleRowSelectedProps();
  return (
    <Checkbox
      name={title}
      data-testid={`toggleRowCheckbox-${row.id}`}
      style={{
        ...style,
        ...(row.state?.isDisabled ? { cursor: 'default' } : {}),
      }}
      onClick={(e) => e.stopPropagation()}
      onChange={(_val, e) => {
        e.stopPropagation();
        onChange(e);
      }}
      checked={indeterminate ? Checkbox.INDETERMINATE : checked}
      disabled={row.state.isDisabled}
    />
  );
};

type Options<D extends Record<string, unknown>> = {
  defaultSize: TableSize;
  columns: ColumnInterface<D>[];
  isPaginatable?: boolean;
};

export const MULTI_SELECT_COLUMN_ID = 'selectable';

function createSelectableColumns<D extends Record<string, unknown>>({
  defaultSize,
  columns,
  isPaginatable,
}: Options<D>): PluginHook<D> {
  const useMultiSelectColumn: PluginHook<D> = (hooks) => {
    hooks.visibleColumns.push((cols, meta) => {
      // On first render meta.instance does not have tableSizeTheme defined because this runs before the plugins are loaded
      // If we start to see bugs with the width of this column in the future this default is probably why
      const iconCellWidth =
        meta.instance.tableSizeTheme?.iconCellWidth ??
        TABLE_SIZE_THEMES[defaultSize].iconCellWidth;
      return [
        {
          id: MULTI_SELECT_COLUMN_ID,
          disableResizing: true,
          maxWidth: 0,
          width: iconCellWidth,
          minWidth: iconCellWidth,
          Header: renderCheckboxHeaderCell({ isPaginatable }),
          Cell: renderCheckboxBodyCell,
          ...(columns.some(({ sticky }) => sticky === STICKY_TYPES.LEFT)
            ? { sticky: STICKY_TYPES.LEFT }
            : {}),
        },
        ...cols,
      ];
    });
  };
  useMultiSelectColumn.pluginName = 'useMultiSelectColumn';
  return useMultiSelectColumn;
}

export default createSelectableColumns;
